import { toSnakeCase } from "./Formatting";
import { cleanValue, canonicalize } from "./Parsing";
import { loadDriversStore } from "./Store";

// Internal helper to denamespace FormData keys (kept local)
function denamespaceFormKey(key: string): { scope: string | null; rest: string } {
  // Matches: "<prefix>:<tcCode>:<voyageNo>:<scope>:<rest>"
  const m = key.match(/^[^:]+:[^:]+:[^:]+:(quantity|impact|rate|option|comment):(.*)$/i);
  if (!m) return { scope: null, rest: key };
  return { scope: m[1].toLowerCase(), rest: m[2] };
}

export function getDriversWithDataFromForm(
  formData: Record<string, string>,
  drivers: Array<{ name: string }>,
  opts?: { includeComments?: boolean },
): string[] {
  const includeComments = opts?.includeComments ?? true;

  const driverSnakeMap = new Map<string, string>(drivers.map((d) => [canonicalize(toSnakeCase(d.name)), d.name]));

  const used = new Set<string>();

  for (const [rawKey, rawValue] of Object.entries(formData)) {
    if (rawValue == null) continue;

    const { scope, rest } = denamespaceFormKey(rawKey);

    if (includeComments) {
      const commentRest = scope === "comment" ? rest : rawKey;
      if (commentRest.endsWith("-Comments")) {
        const driverSnake = canonicalize(commentRest.replace(/-Comments$/, ""));
        const driverName = driverSnakeMap.get(driverSnake);
        if (driverName && rawValue.trim().length > 0) {
          used.add(driverName);
        }
      }
    }

    const numericKey = scope === "quantity" || scope === "impact" ? rest : rawKey;
    const parts = numericKey.split("-");
    if (parts.length >= 3) {
      const field = parts[1];
      if (field !== "Quantity" && field !== "Impact") continue;

      const driverSnakeCandidate = canonicalize(parts.slice(2).join("-"));
      const driverName = driverSnakeMap.get(driverSnakeCandidate);
      if (!driverName) continue;

      const val = cleanValue(rawValue, field === "Impact");
      if (typeof val === "number" && Number.isFinite(val) && Math.abs(val) > 0) {
        used.add(driverName);
      }
    }
  }

  // Keep original order
  const usedSet = used;
  return drivers.map((d) => d.name).filter((name) => usedSet.has(name));
}

/** Check presence of driver data in the Drivers store only. */
export function hasDriverDataInLocalStorage(
  tcCode: string | undefined,
  voyageNo: string | undefined,
  driverName: string,
  opts?: { includeComments?: boolean },
): boolean {
  const store = loadDriversStore(tcCode, voyageNo);
  const driverSnake = canonicalize(toSnakeCase(driverName));
  const entry = store[driverSnake];
  if (entry) {
    const includeComments = opts?.includeComments ?? true;
    const hasComment = includeComments && entry.comments && entry.comments.trim().length > 0;
    const hasNumbers = Object.values(entry.categories || {}).some(
      (line) =>
        (typeof line.qty === "number" && Math.abs(line.qty) > 0) ||
        (typeof line.impact === "number" && Math.abs(line.impact) > 0),
    );
    if (hasComment || hasNumbers) return true;
  }
  return false;
}
